import React, { useState, useEffect } from "react";
import { apiJson } from "../utils/api";
import { useT } from "../i18n/LanguageContext";

const DataTable = ({
  title,
  apiPath,
  columns,
  formFields,
  transformSubmit,
}) => {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState("");

  const fetchItems = async () => {
    try {
      const data = await apiJson(apiPath);
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [apiPath]);

  const resetForm = () => {
    setFormData({});
    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  const handleEdit = (item) => {
    const data = {};
    formFields.forEach((f) => {
      if (f.separator) return;
      let val = item[f.key];
      if (f.type === "date" && val) {
        val = val.split("T")[0];
      }
      data[f.key] = val ?? "";
    });
    setFormData(data);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = transformSubmit ? transformSubmit(formData) : formData;

    try {
      if (editingId) {
        await apiJson(`${apiPath}/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await apiJson(apiPath, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      resetForm();
      fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("errors.deleteConfirm"))) return;
    try {
      await apiJson(`${apiPath}/${id}`, { method: "DELETE" });
      fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const renderFieldValue = (item, col) => {
    if (col.render) return col.render(item);
    const val = item[col.key];
    if (val === null || val === undefined) return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return String(val);
  };

  if (loading) return <p className="text-gray-500">{t("common.loading")}</p>;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-800">{title}</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm"
        >
          {t("common.addNew")}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow mb-4 space-y-3">
          <h3 className="font-semibold text-gray-700">
            {editingId ? t("common.edit") : t("common.addNew")}
          </h3>
          {(() => {
            // Group fields by separators
            const groups = [];
            let current = { heading: null, fields: [] };
            for (const f of formFields) {
              if (f.separator) {
                if (current.fields.length || current.heading) groups.push(current);
                current = { heading: f.separator, fields: [] };
              } else {
                current.fields.push(f);
              }
            }
            if (current.fields.length || current.heading) groups.push(current);

            const renderField = (field) => (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {field.label}
                </label>
                {field.type === "select" ? (
                  <select
                    value={formData[field.key] ?? ""}
                    onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                    className="w-full px-2 py-1.5 border rounded text-sm"
                    required={field.required}
                  >
                    <option value="">{t("common.select")}</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    checked={formData[field.key] ?? field.defaultValue ?? false}
                    onChange={(e) => setFormData({ ...formData, [field.key]: e.target.checked })}
                    className="mt-1"
                  />
                ) : (
                  <input
                    type={field.type || "text"}
                    value={formData[field.key] ?? ""}
                    onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                    required={field.required}
                    step={field.type === "number" ? field.step : undefined}
                    className="w-full px-2 py-1.5 border rounded text-sm"
                  />
                )}
              </div>
            );

            return groups.map((g, gi) => (
              <div key={gi} className={gi > 0 ? "pt-3 border-t border-gray-200" : ""}>
                {g.heading && (
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {g.heading}
                  </h4>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {g.fields.map(renderField)}
                </div>
              </div>
            ));
          })()}
          <div className="flex gap-2">
            <button type="submit" className="bg-indigo-600 text-white px-4 py-1.5 rounded text-sm hover:bg-indigo-700">
              {editingId ? t("common.update") : t("common.create")}
            </button>
            <button type="button" onClick={resetForm} className="bg-gray-200 text-gray-700 px-4 py-1.5 rounded text-sm hover:bg-gray-300">
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {columns.map((col) => (
                <th key={col.key || col.label} className="text-left px-3 py-2 font-medium text-gray-600">
                  {col.label}
                </th>
              ))}
              <th className="text-left px-3 py-2 font-medium text-gray-600">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-3 py-4 text-center text-gray-400">
                  {t("common.noRecords")}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  {columns.map((col) => (
                    <td key={col.key || col.label} className="px-3 py-2">
                      {renderFieldValue(item, col)}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-indigo-600 hover:underline text-xs mr-3"
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:underline text-xs"
                    >
                      {t("common.delete")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
